import lo from 'lodash';
import dayjs from 'dayjs';
import { safeParse, isBetween } from './utils';
import { buyNoticeJSON } from './constant';
import { getProduct } from '@/utils/product';

/**
 * 基础
 */
function basicFormat({
  attributes: attrs,
  categories,
  status,
  isStock,
  tagList,
  productList = [],
  saleAttributesList = [],
  productCornerLabel
}) {
  const attributes = lo
    .chain(attrs)
    .map(({ attrCode, attrName, attrValue: v, ...others }, index) => {
      const { valueName, valueCode } = (v && v[0]) || {};
      return {
        attrCode,
        attrName,
        valueName,
        valueCode,
        index,
        ...others
      };
    })
    .keyBy('attrCode')
    .value();

  const sizeAttrs = safeParse(attributes.PDP_CM?.valueName) || {};
  const isBlindBox = attributes.PDP_BLIND_BOX?.valueName === '是';

  return {
    // 是否为不存在的商品 或者 当前用户不可见的商品
    isAvailable: lo
      .chain([productList, saleAttributesList])
      .every(a => a && !!a.length)
      .value(),
    // 是否已经展示过商品首屏图片
    isDisplayedTopBanner: false,
    // 会员专享
    isMemberOnly: !!lo.find(tagList, { displayName: '会员专享' }),
    // 预热活动
    isHasPerHotActivity: false,
    // 优惠券列表(单独的接口去查)
    couponList: [],
    // 购物须知
    buyNoticeJSON,
    // 是否有库存
    isStock,
    // 属性
    attributes,
    // 下架
    isOutofSale: status === 0,
    // 描述
    productSubName: attributes.PDP_SUB_TITLE?.valueName,
    // 闷包
    isBlindBox,
    // 新人优惠券券面额,
    couponValueForNewMember: 20,
    // 商品目录
    categories:
      categories && categories.length
        ? lo
            .chain(categories)
            .get('0')
            .thru(({ attributeValues, categoryPath }) => {
              const codes = lo.split(categoryPath, '>');
              const labels = lo
                .chain(attributeValues)
                .get('0.attrValue.0.valueName')
                .split('>')
                .value();

              return lo
                .chain(codes)
                .map((categoryCode, i) => ({ categoryName: labels[i], categoryCode, extCode: '' }))
                .value();
            })
            .value()
        : [],
    // 尺码属性
    sizeAttrs,
    // 是否有尺码报告
    isHasSizeReport: !!sizeAttrs.Report?.columnsData,
    // 尺码数据
    sizeReportData: [sizeAttrs.Report?.columns || [], ...(sizeAttrs.Report?.columnsData || [])],
    // 商品详情图文
    fullDescription: safeParse(attributes.PDP_INTRO_SPXQ?.valueName),
    // 角标
    productCornerLabel: {
      ...(productCornerLabel || {}),
      visible: productCornerLabel && !['06', '07'].includes(productCornerLabel.type)
    }
  };
}

/**
 * 商品信息
 */
function pdtInfoFormat({ attributes }) {
  const preset = [
    '科技介绍',
    '商品细节',
    '材质说明',
    '商品说明',
    '材质规格',
    '产品特点',
    '洗涤说明',
    '自然干燥',
    '适合运动',
    '适合场地',
    '温馨提示'
  ];

  return {
    // 商品信息
    productInfoItems:
      lo
        .chain(attributes)
        .filter(({ attrName }) => lo.includes(preset, attrName))
        .map(item => {
          const sort = preset.findIndex(str => str === lo.trim(item.attrName));
          return {
            ...item,
            sort,
            label: item.attrName,
            list: lo.chain(item.valueCode).split('\n').map(lo.trim).value()
          };
        })
        .sortBy('sort')
        .value() || []
  };
}

/**
 * 价格
 * 秒杀价格
 * 会员专享价
 */
function priceFormart({ spuCampaign, salePrice, productPrice, attributes }) {
  // 最终价格
  const finalPrice = Number(attributes.SHOW_ESTIMATED_PRICE?.valueCode);

  // 秒杀价格
  let showMiaoshaPrice = spuCampaign?.activityType === '6000';
  let miaoshaRestTime = 0;
  let miaoshaEndTime = '';
  let miaoshaStartTime = '';
  let miaoshaPrice = null;

  if (showMiaoshaPrice) {
    const { activityBegin: start, activityEnd: end, flashPrice } = spuCampaign || {};

    miaoshaPrice = flashPrice;
    showMiaoshaPrice = true;
    miaoshaStartTime = dayjs(start).format('YYYY.MM.DD');
    miaoshaEndTime = dayjs(end).format('YYYY.MM.DD');
    miaoshaRestTime = Math.abs(dayjs().diff(end, 'millisecond'));
  }

  // 会员价格
  const showMemberPrice = spuCampaign?.activityType === '6001';
  // 会员优先购买
  const showMemberROFR = spuCampaign?.activityType === '6002';

  return {
    salePrice, // 售价
    productPrice, // 吊牌价
    showProductPrice: Number(productPrice) > Number(salePrice),

    // 预估到手价
    showFinalPrice:
      finalPrice > 0 &&
      (attributes.SHOW_ESTIMATED_PRICE_START_TIME?.valueCode
        ? dayjs().isAfter(attributes.SHOW_ESTIMATED_PRICE_START_TIME.valueCode)
        : true) &&
      (attributes.SHOW_ESTIMATED_PRICE_END_TIME?.valueCode
        ? dayjs().isBefore(attributes.SHOW_ESTIMATED_PRICE_END_TIME.valueCode)
        : true),
    finalPrice,

    // 秒杀
    showMiaoshaPrice,
    miaoshaRestTime,
    miaoshaPrice,
    miaoshaEndTime,
    miaoshaStartTime,

    // 会员专享价
    showMemberPrice,
    memberPrice: spuCampaign?.flashPrice,
    memberLevelText: spuCampaign?.memberLevelText,
    isNextLevelActivity: spuCampaign?.isNextLevelActivity,
    memberPriceText: spuCampaign?.memberPriceText,

    // 优先购买权
    showMemberROFR,
    // 暂时无法购买
    dontAllowBuyNow: showMemberPrice || showMemberROFR ? spuCampaign?.canBuy === false : false
  };
}

/**
 * 预售
 * 预热
 */
function preHotAndSaleActivityFormat({ spuCampaign, isBlindBox, productType }) {
  const isHasPerHotActivity = spuCampaign?.activityType === '2000';
  const isHasPerSaleActivity = spuCampaign?.activityType === '05';

  return {
    // 预热
    isHasPerHotActivity,
    // 预售
    isHasPerSaleActivity,
    // 是否可以加入购车
    isAdd2Shopcart: !(isHasPerSaleActivity || isBlindBox) && productType !== '4'
  };
}

/**
 * 促销活动
 */
function activityFormat({ productActivities: list = [] }) {
  return {
    availableProductActivities: lo
      .chain(list)
      .filter(item => !!item.activityName)
      .sortBy('activitySort')
      .value()
  };
}

/**
 * 销售属性
 */
function saleAttributesFormat({ saleAttributesList, skuList, productNo, isStock }) {
  const getValue = (attrCode, mapFn) => {
    return (
      lo.chain(saleAttributesList).find({ attrCode }).get('attrValue').map(mapFn).value() || []
    );
  };

  /**
   * 颜色属性
   */
  const colorSaleAttrs = getValue('color', item => {
    return {
      ...item,
      src: item.valueImage,
      perfixProductNo: lo.chain(item.productNo).split('-').get('0').value(),
      fullAttrDisplayName: lo.join([item.valueName, '(', item.valueCode, ')'], '')
    };
  });

  /**
   * 尺码属性
   */
  const isAvailableSize = ({ status, availableQty }) => status && availableQty > 0;
  const sizeSaleAttrs = getValue('SIZE', item => {
    const target =
      lo.find(skuList, ({ saleAttributes: { SIZE } }) => item.valueCode === SIZE) || {};
    return {
      ...target,
      ...item,
      disabled: !isAvailableSize(target)
    };
  }).filter(item => item.skuNo);

  return {
    // 颜色属性
    colorSaleAttrs,
    // 选中的颜色
    activedColorAttr: lo.find(colorSaleAttrs, { productNo }) || {},
    // 尺码属性
    sizeSaleAttrs,
    // 选中的尺码属性
    activedSizeAttr: (isStock && sizeSaleAttrs.length === 1 && sizeSaleAttrs?.[0]) || null
  };
}

/**
 * 加购
 */
function pickupFormat() {
  return {
    // 加购物逻辑
    pickupData: {
      // 数量
      quantity: 1,
      // 各种消息状态
      messages: [
        { label: '超出可加购数量', value: 'over-max-stock', show: false },
        {
          label: '库存紧张',
          value: 'danger-stock',
          show: false
        },
        {
          label: '暂无库存',
          value: 'no-stock',
          show: false
        }
      ],
      max: 5, // 最大可加购数量
      min: 1, // 最小可加购数量
      // 库存紧张数量极值
      maxDangeStock: 10,
      // 加购的loading
      loading: false,
      // 显示选择尺码的提示
      pleasePickSize: false,
      // 当前所选尺码无库存
      outOfStock: false,
      skuCode: null,
      salePrice: null,
      productPrice: null
    }
  };
}

/**
 * 商品图片
 */
function mediaFormat({ mediaLink, mobileMediaLink, attributes }) {
  const split = (s, videoSrc = '') =>
    lo
      .chain(s || '')
      .split(',')
      .map(url => {
        const urlObj = new URL(url.trim());
        const fileExtension = urlObj.pathname.split('?')?.[0].split('.').pop();
        const isVideoFormat = ['mp4', 'avi', 'mov', 'wmv'].includes(fileExtension);
        return {
          url: urlObj.href,
          type: isVideoFormat ? 'urlVideo' : 'image'
        };
      })
      .thru(v => {
        const list = Array.isArray(v) ? v : [];

        videoSrc &&
          list.unshift({
            url: videoSrc,
            type: 'video'
          });

        return list;
      })
      .value() || [];
  const getVideoSrc = (key = 'PDP_VIDEO_ZTSP') => attributes[key]?.valueCode || '';
  return {
    pcMediaLink: split(mediaLink, getVideoSrc()),
    mobileMediaLink: split(mobileMediaLink, getVideoSrc('PDP_VIDEO_ZTSP_MOB'))
  };
}

/**
 * 赠品
 */
function giftsFormat({ productActivities }) {
  const pickMedia = (str: string) => lo.chain(str).split(',').get(0).value();
  const acts = lo
    .chain(productActivities)
    .sortBy('activitySort')
    .filter(item => {
      const { activityBegin, activityEnd, giveaways, activityType } = item;

      return activityType === '01' && !!giveaways?.length && isBetween(activityBegin, activityEnd);
    })
    .reduce((ctr, { giveaways, ...others }) => {
      if (giveaways.length > 1) {
        giveaways.forEach(giveaway => {
          ctr.push({ ...others, sku: giveaway, giveaways });
        });
      } else {
        ctr.push({ ...others, sku: giveaways?.[0], giveaways });
      }

      return ctr;
    }, [])
    .map(target => {
      target.startTime = dayjs(target.activityBegin).format('M.DD HH:mm');
      target.endTime = dayjs(target.activityEnd).format('M.DD HH:mm');
      target.sku.mediaLink = pickMedia(target.sku.mediaLink);

      lo.forEach(target.giveaways, item => {
        item.mediaLink = pickMedia(item.mediaLink);
      });

      return { ...target };
    })
    .value();

  return {
    isHasGift: !!acts.length,
    giftActivities: acts
  };
}

/**
 * 其他
 */
function endFormat() {
  return {
    activeColor: 'removed',
    activeSkuData: 'removed',
    skuList: 'removed',
    saleAttributesList: 'removed',
    saleAttributes: 'removed',
    productList: 'removed'
  };
}

export async function getProductDetails(options) {
  const handlerFac = fn => data => ({ ...data, ...fn(data) });
  const product = getProduct();
  const { originalDataInfo: data = {}, ...others } = await product.init(options);
  const flow = lo.flow(
    lo.map(
      [
        basicFormat,
        pdtInfoFormat,
        priceFormart,
        preHotAndSaleActivityFormat,
        activityFormat,
        saleAttributesFormat,
        pickupFormat,
        mediaFormat,
        giftsFormat,
        endFormat
      ],
      handlerFac
    )
  );

  return flow({
    ...data,
    ...others
  });
}
